import React, { memo } from 'react'
import { Star } from 'lucide-react'
import { formatCurrency, getRealDate, fixEncoding } from '../../utils/formatting'
import { calculateAverageRentPerSqm } from '../../utils/game/rentCalculations'

/**
 * Optimierte PortfolioPropertyCard-Komponente mit React.memo
 * 
 * Verhindert unnötige Re-Renders für Portfolio-Immobilien.
 * Wichtig für große Listen von eigenen Immobilien.
 */
const PortfolioPropertyCard = memo(({ 
  property,
  isSelected = false,
  canCompare = true,
  onSelect,
  onOpenModal,
  onCompare,
  onRenovate,
  onSell,
  onRent,
  onCancelTenant,
  favorites = [],
  onToggleFavorite,
  skillModifiers = {},
  unlockedTalents = [],
  getPropertyTypeIcon,
  gameState = {},
  selectedProperties = [],
  hasBulkOperations = false,
  talentPointsSpent = {},
  formatCurrency: formatCurrencyProp
}) => {
  const format = formatCurrencyProp || formatCurrency
  
  const problemCase = property.zustand < 55
  const estimatedRenovationCost = format(
    (property.renovierungKosten ?? Math.round(property.wert * 0.08)) * (skillModifiers.renovation || 1)
  )
  const roi = property.roi 
    ? (property.roi * 100).toFixed(1) 
    : ((property.miete * 12) / property.wert * 100).toFixed(1)
  
  const roiColor = roi >= 8 
    ? 'text-emerald-400' 
    : roi >= 5 
      ? 'text-amber-400' 
      : 'text-slate-400'
  
  const conditionColor = property.zustand >= 80 
    ? 'bg-emerald-500' 
    : property.zustand >= 60 
      ? 'bg-amber-500' 
      : 'bg-red-500'
  
  const isFavorite = favorites.includes(property.id)
  
  return (
    <div
      onClick={(event) => {
        if (!hasBulkOperations || !event.target.closest('button, input, a, [role="button"]')) {
          onOpenModal?.(property)
        }
      }}
      className={`rounded-2xl p-4 smooth-transition cursor-pointer relative hover-lift button-click group ${
        isSelected
          ? 'border-2 border-accent bg-accent/10'
          : problemCase 
            ? 'border-2 border-amber-500/50 bg-amber-500/10 hover:border-amber-500/70 hover:bg-amber-500/15' 
            : 'border border-white/5 bg-slate-800/60 hover:border-accent/50 hover:bg-slate-800/80'
      } hover:shadow-lg hover:shadow-accent/10`}
    >
      {hasBulkOperations && (
        <div
          onClick={(e) => {
            e.stopPropagation()
            onSelect?.(property.id, !isSelected)
          }}
          className={`absolute top-3 right-3 rounded-full p-1.5 transition-all cursor-pointer z-10 backdrop-blur-sm ${
            isSelected
              ? 'bg-accent/95 text-white shadow-lg shadow-accent/50 border-2 border-white/20'
              : 'bg-slate-900/95 text-slate-400 hover:bg-slate-800/95 hover:text-accent border-2 border-white/20'
          }`}
          title={isSelected ? 'Auswahl aufheben' : 'Auswählen'}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              e.stopPropagation()
              onSelect?.(property.id, !isSelected)
            }
          }}
        >
          {isSelected ? (
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
      )}
      
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {getPropertyTypeIcon?.(property.typ)}
            <p className="text-slate-400 text-xs uppercase">{property.stadtteil}</p>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onToggleFavorite?.(property.id)
              }}
              className="ml-auto"
            >
              <Star
                size={14}
                className={isFavorite ? 'text-amber-400 fill-amber-400' : 'text-slate-500'}
              />
            </button>
          </div>
          <h4 className="text-lg font-semibold text-slate-100">{property.typ}</h4>
          {((property.typ === 'Studentenwohnheim' || property.typ === 'Mehrfamilienhaus') && property.apartments && property.apartments.length > 0) && (
            <p className="text-xs text-slate-400 mt-1">
              {property.typ === 'Studentenwohnheim' 
                ? `${property.apartments.length} Zimmer • ${property.apartments.filter(a => !a.leerstand).length} vermietet`
                : `${property.apartments.length} Wohnung${property.apartments.length !== 1 ? 'en' : ''} • ${property.apartments.filter(a => !a.leerstand).length} vermietet`}
            </p>
          )}
          <div className="flex items-center gap-2 mt-1">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                property.leerstand ? 'bg-amber-500/20 text-amber-200' : 'bg-emerald-500/20 text-emerald-100'
              }`}
            >
              {property.leerstand ? 'Leerstand' : 'Vermietet'}
            </span>
            <span className="text-xs text-slate-500">
              {fixEncoding(property.financing) ?? 'Bar'}
            </span>
            {property.purchaseDate ? (
              <span className="text-xs text-slate-500">
                Kauf: {property.purchaseDate.fullDate}
              </span>
            ) : property.gekauftImMonat ? (
              <span className="text-xs text-slate-500">
                Kauf: {getRealDate(property.gekauftImMonat, 1, gameState.startDate).fullDate}
              </span>
            ) : null}
          </div>
        </div>
        <div className="text-right pr-12">
          <p className="text-xl font-bold text-slate-100">{Math.round(property.wert).toLocaleString('de-DE')} €</p>
          <p className="text-xs text-slate-400 mt-1">WERT</p>
        </div>
      </div>
      
      <div className="mt-4 grid grid-cols-4 gap-2 text-sm">
        <div>
          <p className="text-slate-500 text-xs">Miete</p>
          <p className="text-emerald-400 font-medium">{format(property.miete)}</p>
          <p className="text-xs text-slate-500">/Monat</p>
        </div>
        <div>
          <p className="text-slate-500 text-xs">QM</p>
          <p className="text-slate-300 font-medium">{property.area ?? 'N/A'} m²</p>
          {property.area && property.miete ? (
            <div className="relative group">
              <p className="text-xs text-slate-500 cursor-help">
                {format(property.miete / property.area)}/m²
              </p>
              {unlockedTalents.includes('rental-11') && (
                <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-48 p-2 rounded-lg bg-slate-900 border border-accent/40 shadow-lg text-xs">
                  <div className="text-slate-300 font-semibold mb-1">Mietpreis-Analyse</div>
                  <div className="text-slate-400 space-y-1">
                    <div className="flex justify-between">
                      <span>Aktuell:</span>
                      <span className="text-slate-300">{format(property.miete / property.area)}/m²</span>
                    </div>
                    <div className="flex justify-between border-t border-white/10 pt-1">
                      <span>Durchschnitt:</span>
                      <span className="text-emerald-400 font-semibold">
                        {format(calculateAverageRentPerSqm(property) || 0)}/m²
                      </span>
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1 pt-1 border-t border-white/5">
                      Potenzial bei Neuvermietung
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              {property.pricePerSqm ? `${format(property.pricePerSqm)}/m²` : ''}
            </p>
          )}
        </div>
        <div>
          <p className="text-slate-500 text-xs">Zustand</p>
          <div className="flex items-center gap-1">
            <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${conditionColor} transition-all`}
                style={{ width: `${property.zustand}%` }}
              />
            </div>
            <span className="text-xs text-slate-300">{property.zustand}%</span>
          </div>
        </div>
        <div>
          <p className="text-slate-500 text-xs">ROI</p>
          <p className={`${roiColor} font-semibold`}>{roi}%</p>
          <p className="text-xs text-slate-500">p.a.</p>
        </div>
      </div>
      
      <div className="mt-4 flex gap-2 flex-wrap">
        {property.leerstand && onRent && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRent(property)
            }}
            className="flex-1 min-w-[120px] rounded-xl bg-emerald-500/80 py-2 text-sm font-semibold text-white hover:bg-emerald-500 transition"
          >
            Vermieten
          </button>
        )}
        {!property.leerstand && onCancelTenant && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onCancelTenant(property)
            }}
            className="flex-1 min-w-[120px] rounded-xl bg-amber-500/80 py-2 text-sm font-semibold text-white hover:bg-amber-500 transition"
          >
            Kündigen
          </button>
        )}
        {onRenovate && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onRenovate(property)
            }}
            className="flex-1 min-w-[120px] rounded-xl bg-blue-500/80 py-2 text-sm font-semibold text-white hover:bg-blue-500 transition"
          >
            Renovieren ({estimatedRenovationCost})
          </button>
        )}
        {canCompare && onCompare && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onCompare(property)
            }}
            className="flex-1 min-w-[120px] rounded-xl bg-purple-500/80 py-2 text-sm font-semibold text-white hover:bg-purple-500 transition"
          >
            Vergleichen
          </button>
        )}
        {onSell && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onSell(property)
            }}
            className="flex-1 min-w-[120px] rounded-xl bg-red-500/80 py-2 text-sm font-semibold text-white hover:bg-red-500 transition"
          >
            Verkaufen
          </button>
        )}
      </div>
    </div>
  )
}, (prevProps, nextProps) => {
  // Custom comparison function für React.memo
  return (
    prevProps.property.id === nextProps.property.id &&
    prevProps.property.wert === nextProps.property.wert &&
    prevProps.property.miete === nextProps.property.miete &&
    prevProps.property.area === nextProps.property.area &&
    prevProps.property.zustand === nextProps.property.zustand &&
    prevProps.property.leerstand === nextProps.property.leerstand &&
    prevProps.property.stadtteil === nextProps.property.stadtteil &&
    prevProps.property.typ === nextProps.property.typ &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.canCompare === nextProps.canCompare &&
    prevProps.favorites?.length === nextProps.favorites?.length &&
    prevProps.favorites?.includes(prevProps.property.id) === nextProps.favorites?.includes(nextProps.property.id) &&
    prevProps.unlockedTalents?.length === nextProps.unlockedTalents?.length
  )
})

PortfolioPropertyCard.displayName = 'PortfolioPropertyCard'

export default PortfolioPropertyCard

